/**
 * AIPlatformPage — the hidden /ai section shell (SuperAdmin only; the route
 * is registered in AppRouter but deliberately absent from every sidebar,
 * drawer, and pageRegistry — navigate manually).
 *
 * Layout: compact left rail (Dashboard, Documents, Review Queue, Skills,
 * Taxonomy, Runs, Improvement) + a React Router v5 <Switch> for content.
 * All data flows through the six docai* callables via useDocAI.
 */
import React from 'react';
import { Switch, Route, NavLink, useRouteMatch, Redirect } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiGrid,
  FiFileText,
  FiInbox,
  FiCpu,
  FiTag,
  FiActivity,
  FiTrendingUp,
} from 'react-icons/fi';

import AIDashboardPage from './AIDashboardPage';
import AIDocumentsPage from './AIDocumentsPage';
import AIDocumentWorkspacePage from './AIDocumentWorkspacePage';
import AIReviewQueuePage from './AIReviewQueuePage';
import AISkillsPage from './AISkillsPage';
import AISkillDetailPage from './AISkillDetailPage';
import AISkillBootstrapPage from './AISkillBootstrapPage';
import AITaxonomyPage from './AITaxonomyPage';
import AIRunsPage from './AIRunsPage';
import AIRunDetailPage from './AIRunDetailPage';
import AIImprovementPage from './AIImprovementPage';

const NAV = [
  { to: '', exact: true, label: 'Dashboard', icon: FiGrid },
  { to: '/documents', label: 'Documents', icon: FiFileText },
  { to: '/review', label: 'Review Queue', icon: FiInbox },
  { to: '/skills', label: 'Skills', icon: FiCpu },
  { to: '/taxonomy', label: 'Taxonomy', icon: FiTag },
  { to: '/runs', label: 'Runs', icon: FiActivity },
  { to: '/improvement', label: 'Improvement', icon: FiTrendingUp },
];

export default function AIPlatformPage() {
  const { path, url } = useRouteMatch();

  const railBg = useColorModeValue('gray.50', 'gray.900');
  const railBorder = useColorModeValue('gray.200', 'gray.700');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeColor = useColorModeValue('blue.600', 'blue.200');
  const inactiveColor = useColorModeValue('gray.600', 'gray.400');
  const brandColor = useColorModeValue('gray.900', 'white');

  return (
    <Flex h="calc(100vh - 150px)" minH="600px" align="stretch" overflow="hidden">
      {/* Left rail */}
      <Box
        w="200px"
        flexShrink={0}
        bg={railBg}
        borderRightWidth="1px"
        borderColor={railBorder}
        py={4}
        px={2}
        overflowY="auto"
      >
        <HStack px={3} mb={4} spacing={2}>
          <Icon as={FiCpu} color={activeColor} boxSize={5} />
          <Text fontWeight="bold" fontSize="sm" color={brandColor}>
            DocAI Platform
          </Text>
        </HStack>
        <VStack align="stretch" spacing={1}>
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={`${url}${item.to}`}
              exact={item.exact}
              style={{ textDecoration: 'none' }}
            >
              {/* NavLink active styling via isActive render prop is v5-awkward
                  with Chakra; use match-based styling inside a Route instead */}
              <Route path={`${path}${item.to}`} exact={item.exact}>
                {({ match }) => (
                  <HStack
                    px={3}
                    py={2}
                    borderRadius="md"
                    bg={match ? activeBg : 'transparent'}
                    color={match ? activeColor : inactiveColor}
                    _hover={{ bg: match ? activeBg : railBorder }}
                    spacing={2}
                  >
                    <Icon as={item.icon} boxSize={4} />
                    <Text fontSize="sm" fontWeight={match ? 'semibold' : 'normal'}>
                      {item.label}
                    </Text>
                  </HStack>
                )}
              </Route>
            </NavLink>
          ))}
        </VStack>
        <Text px={3} mt={6} fontSize="10px" color="gray.500">
          SuperAdmin only. Hidden section — the CLI (`docai`) operates on the same cloud state.
        </Text>
      </Box>

      {/* Content */}
      <Box flex={1} minW={0} overflowY="auto">
        <Switch>
          <Route exact path={path} component={AIDashboardPage} />
          <Route exact path={`${path}/documents`} component={AIDocumentsPage} />
          <Route exact path={`${path}/documents/:documentId`} component={AIDocumentWorkspacePage} />
          <Route exact path={`${path}/review`} component={AIReviewQueuePage} />
          <Route exact path={`${path}/skills`} component={AISkillsPage} />
          <Route exact path={`${path}/skills/new`} component={AISkillBootstrapPage} />
          <Route exact path={`${path}/skills/:skillName`} component={AISkillDetailPage} />
          <Route exact path={`${path}/taxonomy`} component={AITaxonomyPage} />
          <Route exact path={`${path}/runs`} component={AIRunsPage} />
          <Route exact path={`${path}/runs/:runId`} component={AIRunDetailPage} />
          <Route exact path={`${path}/improvement`} component={AIImprovementPage} />
          <Redirect to={path} />
        </Switch>
      </Box>
    </Flex>
  );
}
